// organigrammKamera.ts — Zoom und Ausschnitt des Organigramms als reine Rechnung.
//
// Die Kamera ist ein Mittelpunkt in Zeichenflächen-Koordinaten plus ein Zoom.
// 100 % heißt: eine SVG-Einheit ist ein CSS-Pixel — das Kästchen erscheint so
// groß, wie es gezeichnet ist. Die viewBox ergibt sich aus Kamera und
// Panelgröße und hat damit immer das Seitenverhältnis des Panels; ein
// `preserveAspectRatio="meet"` quetscht nichts mehr zusammen.

export type Punkt = { x: number; y: number };
export type Groesse = { w: number; h: number };
export type Rahmen = { x: number; y: number; w: number; h: number };
export type Kamera = { zoom: number; cx: number; cy: number };

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 4;
/** Darunter ist die Schrift nicht mehr lesbar — die Startansicht geht nicht tiefer. */
export const START_ZOOM_MIN = 0.3;
/** Rand um den Graphen beim Einpassen, in Bildschirm-Pixeln. */
const EINPASS_RAND = 24;
/** So viel vom Graphen bleibt beim Ziehen mindestens im Bild, in Bildschirm-Pixeln. */
const SICHTBAR_REST = 80;

/** Die Stufen der Knöpfe − und +. */
export const ZOOM_STUFEN = [0.1, 0.15, 0.2, 0.25, 0.33, 0.5, 0.67, 0.8, 1, 1.25, 1.5, 2, 2.5, 3, 4];

export const zoomBegrenzen = (z: number): number =>
  Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number.isFinite(z) ? z : 1));

/** Nächste Stufe nach oben (+1) bzw. unten (−1), ausgehend vom aktuellen, evtl. krummen Zoom. */
export function zoomStufe(z: number, richtung: 1 | -1): number {
  const eps = 0.005;
  if (richtung > 0) return ZOOM_STUFEN.find((s) => s > z + eps) ?? ZOOM_MAX;
  return [...ZOOM_STUFEN].reverse().find((s) => s < z - eps) ?? ZOOM_MIN;
}

/** Zoom, bei dem der ganze Graph ins Panel passt — nie über 100 % (kleine Firma bleibt klein). */
export function einpassZoom(inhalt: Rahmen, panel: Groesse): number {
  const w = Math.max(panel.w - 2 * EINPASS_RAND, 1);
  const h = Math.max(panel.h - 2 * EINPASS_RAND, 1);
  return zoomBegrenzen(Math.min(1, w / Math.max(inhalt.w, 1), h / Math.max(inhalt.h, 1)));
}

const mitte = (r: Rahmen): Punkt => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });

/** „Einpassen": alles im Bild, auch wenn es dafür sehr klein wird. */
export function eingepassteKamera(inhalt: Rahmen, panel: Groesse): Kamera {
  const m = mitte(inhalt);
  return { zoom: einpassZoom(inhalt, panel), cx: m.x, cy: m.y };
}

/**
 * Startansicht: einpassen, aber nicht kleiner als {@link START_ZOOM_MIN}. Passt
 * der Graph dann nicht ganz hinein, steht die eigene Firma (`fokus`) in der
 * Mitte — von dort aus sucht man weiter.
 */
export function startKamera(inhalt: Rahmen, panel: Groesse, fokus?: Punkt | null): Kamera {
  const fit = einpassZoom(inhalt, panel);
  if (fit >= START_ZOOM_MIN) return eingepassteKamera(inhalt, panel);
  const ziel = fokus ?? mitte(inhalt);
  return kameraBegrenzen({ zoom: START_ZOOM_MIN, cx: ziel.x, cy: ziel.y }, inhalt, panel);
}

/** Sichtbarer Ausschnitt in Zeichenflächen-Koordinaten (= viewBox). */
export function viewBoxVon(k: Kamera, panel: Groesse): Rahmen {
  const w = panel.w / k.zoom;
  const h = panel.h / k.zoom;
  return { x: k.cx - w / 2, y: k.cy - h / 2, w, h };
}

/** Bildschirm-Punkt (relativ zur linken oberen Panel-Ecke) → Zeichenfläche. */
export function panelZuWelt(p: Punkt, k: Kamera, panel: Groesse): Punkt {
  return { x: k.cx + (p.x - panel.w / 2) / k.zoom, y: k.cy + (p.y - panel.h / 2) / k.zoom };
}

/**
 * Ausschnitt so begrenzen, dass immer etwas vom Graphen sichtbar bleibt: in
 * jeder Richtung ragen mindestens {@link SICHTBAR_REST} Pixel (bzw. der ganze
 * Graph, wenn er kleiner ist) ins Panel.
 */
export function kameraBegrenzen(k: Kamera, inhalt: Rahmen, panel: Groesse): Kamera {
  const achse = (c: number, start: number, laenge: number, panelLaenge: number) => {
    const halb = panelLaenge / (2 * k.zoom);
    const rest = Math.min(SICHTBAR_REST / k.zoom, laenge, panelLaenge / k.zoom);
    const min = start + rest - halb;
    const max = start + laenge - rest + halb;
    if (min > max) return start + laenge / 2;
    return Math.min(max, Math.max(min, c));
  };
  return {
    zoom: k.zoom,
    cx: achse(k.cx, inhalt.x, inhalt.w, panel.w),
    cy: achse(k.cy, inhalt.y, inhalt.h, panel.h),
  };
}

/**
 * Auf `zoom` gehen und dabei den Panel-Punkt `um` festhalten (Mausrad: der
 * Punkt unter dem Cursor bleibt, wo er ist). Ohne `um` zoomt es um die Mitte.
 */
export function zoomen(k: Kamera, zoom: number, panel: Groesse, um?: Punkt): Kamera {
  const z = zoomBegrenzen(zoom);
  if (!um) return { zoom: z, cx: k.cx, cy: k.cy };
  const p = panelZuWelt(um, k, panel);
  const f = k.zoom / z;
  return { zoom: z, cx: p.x - (p.x - k.cx) * f, cy: p.y - (p.y - k.cy) * f };
}

/** Ausschnitt um Bildschirm-Pixel verschieben (Ziehen: der Graph folgt der Maus). */
export const verschieben = (k: Kamera, dxPx: number, dyPx: number): Kamera => ({
  zoom: k.zoom,
  cx: k.cx - dxPx / k.zoom,
  cy: k.cy - dyPx / k.zoom,
});

/** Zoom-Faktor eines Rad-Ereignisses (Trackpad-Pinch liefert kleine deltaY, ein Rad große). */
export function radFaktor(deltaY: number, deltaMode = 0): number {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  const begrenzt = Math.max(-300, Math.min(300, px));
  return Math.exp(-begrenzt * 0.0025);
}

/** Umschließender Rahmen der Kästchen (Mittelpunkt ± halbe Größe) plus Rand. */
export function rahmenUm(
  kaestchen: Array<{ x: number; y: number; w: number; h: number }>,
  rand = 40,
): Rahmen | null {
  if (!kaestchen.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const b of kaestchen) {
    x0 = Math.min(x0, b.x - b.w / 2);
    y0 = Math.min(y0, b.y - b.h / 2);
    x1 = Math.max(x1, b.x + b.w / 2);
    y1 = Math.max(y1, b.y + b.h / 2);
  }
  return { x: x0 - rand, y: y0 - rand, w: x1 - x0 + 2 * rand, h: y1 - y0 + 2 * rand };
}
